import { useEffect, useState, useRef } from "react";
// import InteractiveMap from "../../components/InteractiveMap"
import Icon from "../../components/Icon"
import FormField from "../../components/FormField";
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { useSelector, useDispatch } from "react-redux";
import * as actions from "../../actions"
import { DOMINICA_COUNTRY_ID } from "../../constants/country";
import { alphabeticalSort, capitalizeAllWords } from "../../utils/stringUtils";
import { getCurrentPosition } from "../../utils/geolocation";
import {isMobile } from "../../utils/deviceUtils"
import { Popper } from "@mui/material";
import {v4 as uuid4} from 'uuid'
const CustomPopper = ({ style, ...props }) => {
    return (
      <Popper
        {...props}
        style={{
          ...style,
          zIndex: 10,
          width: props.anchorEl ? props.anchorEl.clientWidth : undefined,
        }}
      />
    );
  };

//filter during search
const filter = createFilterOptions();

/**
 * Get a location involving a settlement / area, lat lon point and a written description
 */
const LocationPicker = ({
    onChange=()=>{},
    countryId=DOMINICA_COUNTRY_ID,
    defaultLocation,
    hasDirections=true,
    directionsLabel=<span>Street address or directions with landmarks e.g. <i>"turn left by the mango tree, then its a blue house with a white gate"</i></span>,
    directionsPlaceholder="",
    errorMessage="",
    collectGPSPosition=false //If true, allows users to select a GPS point on the map
}) => {
    //get the country and the list of settlements within
    const country = useSelector(state => state.countries.countriesById[countryId])
    const {settlements:settlementsMap={}, name:countryName=""} = country || {}
    //the location which will be passed to the parent
    const locationId = useRef(uuid4()) 
    const emptyLocation = {id: locationId.current, directions:"", latLon:null, settlementId:"", city:"", countactNumber:"", countryId, countryName, line1: "", line2: "", usaCity:"", zipCode:""}
    const [location, setLocation] = useState(
        defaultLocation ? {...defaultLocation} : emptyLocation
    )
    const settlementList = Object.entries(settlementsMap)
                                 .map(([id, name]) => ({id, name}))
                                 .sort((sA, sB) => alphabeticalSort(sA.name, sB.name))
    //used to change the selected settlement id
    const [settlementId, setSettlementId] = useState(location.settlementId);
    const [errorText, setErrorText] = useState("");
    //load the settlement when it is selected
    const dispatch = useDispatch()
    useEffect(() => {
        if (settlementId) dispatch(actions.httpFetchSaveSettlement(settlementId))
    }, [settlementId, dispatch])
    //when the settlement changes, update the map center and set a position
    const settlement = useSelector(state => state.settlements.settlementsById[settlementId] || null)
    const [mapCenter, setMapCenter] = useState(null)
    const [position, setPosition] = useState(null)
    useEffect(() => {
        let newLocation = settlementId ? {...location} : emptyLocation
        let mapCenter = newLocation.latLon
        let position = collectGPSPosition ? newLocation.latLon : null
        if (settlement){
            const {lat, lon, id:settlementId="", name:city="" } = settlement
            mapCenter = location.settlementId === settlementId && location.latLon ? location.latLon : {lat, lon}
            position = collectGPSPosition ? mapCenter : null
            newLocation = {
                ...location, 
                settlementId, 
                city, 
                latLon: collectGPSPosition ? mapCenter : null}
        }
        setMapCenter(mapCenter)
        setPosition(position)
        setLocation(newLocation)
    }, [JSON.stringify(settlement), settlementId])
    //allow the user to get their current position and update the map center position
    const handleClickGetCurrentPosition = async () => {
        if (!collectGPSPosition) return false
        try {  
            const currentPos = await getCurrentPosition()
            setPosition(currentPos)
            setMapCenter(currentPos)
        }
        catch(e) {
            alert(`Sorry, we could not find your current location: ${e.message}`)
        }
    }
    //update the location whenever the point's map position changes
    useEffect(() => {
        setLocation(prev => ({...prev, latLon: position}))
    },[JSON.stringify(position)])
    //update the parent
    useEffect(() => {
        onChange(location)
    }, [JSON.stringify(location)])
    return (
        <div className="card-group">
            <div className="card">
                <Autocomplete
                    PopperComponent={CustomPopper}
                    value={settlement}
                    onChange={(_,s) => setSettlementId(s ? s.id : "")}
                    //filter the settlements based on the text typed into the input
                    filterOptions={(settlementList, params) => filter(settlementList, params)}
                    options={settlementList}
                    getOptionLabel={option => capitalizeAllWords(option.name)}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    selectOnFocus
                    clearOnBlur
                    handleHomeEndKeys
                    freeSolo={false} // prevent typing custom values
                    renderOption={(props, option) => <li {...props}>{capitalizeAllWords(option.name)}</li>}
                    sx={{ width: "100%" }}
                    renderInput={(params) => <div ref={params.InputProps.ref} className={`${errorText ? "error" : ""} margin-bottom-05em`}>
                                                <label {...params.InputLabelProps} className='label'>Search for a town or city{countryName ? ` in ${capitalizeAllWords(countryName)}` : ""} </label>
                                                <input 
                                                    {...params.inputProps} 
                                                    className='input' 
                                                />
                                                {errorText ? <div className="errorText" >{errorText}</div>: null}
                                            </div>
                                            }
                />
            </div>
            {
                errorMessage ?
                <div className="card">
                    {errorMessage}
                </div>
                :
                null
            }
            {
                settlement && collectGPSPosition?
                <div>
                    <div className="label margin-top-5px">
                        <ol>
                            <li>Click "Find your current location", to jump to where you are right now.</li>
                            <li>Press, hold and drag the blue point to your exact location - sometimes GPS is not exact.</li>
                            <li>Use both fingers to zoom on the map if you need to see more around you</li>
                        </ol>
                    </div>
                    <div className="position-relative">
                        <button className="button icon icon-left success position-absolute z-index-10 top-5px right-5px" 
                                onClick={handleClickGetCurrentPosition}
                        >
                            <Icon icon="my-location"/>
                            Find your current location
                        </button>
                        <div style={{ width: "100%", height: isMobile() ? "200px" : "300px", position: "relative", zIndex: 1 }}>
                        {/* <InteractiveMap
                            center={mapCenter}
                            latLon={position}
                            zoom={17}
                            onChangeLatLon={latLon => setPosition(latLon)}
                        /> */}
                        </div>
                    </div>
                </div>
                :
                null
            }
            {
                hasDirections && settlement && !errorMessage?
                <div className="card">
                    <FormField 
                        value={location.directions} 
                        label={directionsLabel}
                        placeholder={directionsPlaceholder}
                        type="textarea"
                        onChange={e => setLocation({...location, directions: e.target.value})}
                    />
                </div>
                :
                null
            }
        </div>
    )
}

export default LocationPicker